import { Fragment, ReactNode, useEffect, useRef } from "react"

import PropTypes from "prop-types"
import ContentLoader from "react-content-loader"
import { View, Text, TouchableOpacity } from "react-native"
import { useSelector } from "react-redux"

import { highlightTypes } from "@configs/enums"
import { selectUserId } from "@selectors/activeTeeup"
import SlimScroll from "@ui/slimScroll"

import ConversationHeader from "./ConversationHeader"
import PinnedMessagesScreen from "./PinnedMessagesScreen"
import { SuggestionsScreen } from "./SuggestionsScreen"
import UnreadBar from "./UnreadBar"
import UserMentionsScreen from "./UserMentionsScreen"
import UserNudgesScreen from "./UserNudgesScreen"
import { DiscussionStyles, CardStyles } from "./discussionStyles"

// CONV-PARITY 2026-07-03 (QJ): filter set + order now mirrors teeup-mobile's Conversation
// tab bar exactly (app/components/teeup/chat/index.tsx `tabs`) -- Chat, All activity, Pinned,
// Mentions, Nudges, All suggestions. "Chat" keeps id 0 (unchanged behavior: the curated stream,
// already minus nudges/retracted-status via selectMessagesWithoutNugdes). "All activity" is new
// (id 5) and renders the raw unfiltered message stream (incl. nudges/system/status messages),
// same as mobile's allTabAllowedConversationTypes = every conversationTypeId.
const tabs = [
  {
    id: highlightTypes.conversation,
    title: "Chat",
  },
  {
    id: highlightTypes.allActivity,
    title: "All activity",
  },
  {
    id: highlightTypes.pinboard,
    title: "Pinned",
  },
  {
    id: highlightTypes.mentions,
    title: "Mentions",
  },
  {
    id: highlightTypes.nudges,
    title: "Nudges",
  },
  {
    id: highlightTypes.suggestions,
    title: "All suggestions",
  },
]

const renderSkeleton = () => {
  return (
    <View style={CardStyles.skeletonContainer}>
      <ContentLoader
        backgroundColor="#E1E9EE"
        foregroundColor="#F2F8FC"
        width="400"
        height="350"
        viewBox="0 0 400 350">
        <rect x="0" y="200" rx="20" ry="20" width="90%" height="100" />
        <rect x="100" y="100" width="120" height="20" />
        <rect x="100" y="150" width="120" height="20" />
      </ContentLoader>
    </View>
  )
}

const renderView = ({ style, ...props }) => {
  return <div id="messages-list" style={{ ...style }} {...props} />
}

interface Props {
  index: number
  messages: any[]
  allMessages?: any[]
  expanded?: boolean
  renderItem: ({ item, index }) => ReactNode
  unreadCount?: number
  unreadBannerEnabled: boolean
  onUnreadClose: () => void
  onUnreadPress: () => void
  testID?: string
  toggleExpansion: () => void
  handleCloseKeyboardOnTouchOutside?: () => void
}

const DiscussionCardView = ({
  index,
  messages,
  allMessages,
  expanded,
  renderItem,
  unreadCount,
  unreadBannerEnabled,
  onUnreadClose,
  onUnreadPress,
  testID,
  toggleExpansion,
  handleCloseKeyboardOnTouchOutside,
}: Props) => {
  const scrollContainerRef = useRef<any>(null)
  const userId = useSelector(selectUserId)

  // CONV-PARITY 2026-07-03: "All activity" (id 5) reuses the same scroll/render machinery
  // as "Chat" (id 0) but over the raw unfiltered message stream (allMessages), matching
  // mobile's allTabAllowedConversationTypes -- see tabs comment above.
  const activeMessages =
    index === highlightTypes.allActivity ? allMessages || [] : messages

  useEffect(() => {
    if (
      !activeMessages?.length ||
      !scrollContainerRef?.current?.scrollToBottom ||
      !scrollContainerRef?.current?.view
    )
      return

    const { view, scrollToBottom } = scrollContainerRef.current

    const lastMessage = activeMessages.at(-1)
    if (
      lastMessage.conversationTypeId === 1 &&
      lastMessage.messageTypeId === 1 &&
      lastMessage.userId === userId
    ) {
      view.style.overflow = "hidden"
      scrollToBottom({
        behavior: "smooth",
        block: "center",
      })
      view.style.overflow = "scroll"
    }
  }, [activeMessages, index])

  const renderMessagesList = messagesList => (
    <View
      style={[
        DiscussionStyles.chatContainer,
        messagesList.length === 0 && { transform: "scaleY(-1)" },
      ]}>
      {unreadBannerEnabled && index === highlightTypes.conversation && (
        <UnreadBar
          count={unreadCount}
          onClose={expanded && onUnreadClose}
          onPress={onUnreadPress}
        />
      )}

      <TouchableOpacity
        style={DiscussionStyles.chatContainerPlaceholder}
        onPress={
          expanded ? handleCloseKeyboardOnTouchOutside : toggleExpansion
        }
      />

      {messagesList.length > 0 && (
        <div
          className="discussion-items__container scroll"
          style={{ height: "100%" }}>
          {/*@ts-ignore */}
          <SlimScroll ref={scrollContainerRef} renderView={renderView}>
            {messagesList.map((item, itemIndex) => (
              <Fragment
                key={`message-${item.id || item.timestamp || item.daybreak}`}>
                {renderItem({ item, index: itemIndex })}
              </Fragment>
            ))}
          </SlimScroll>
        </div>
      )}

      {messagesList.length === 0 && renderSkeleton()}
    </View>
  )

  return (
    <View
      style={CardStyles.cardContent}
      pointerEvents="box-none"
      testID={testID}>
      <ConversationHeader tabs={tabs} />

      <View style={DiscussionStyles.splitter} />

      {index === highlightTypes.conversation && renderMessagesList(messages)}

      {index === highlightTypes.allActivity &&
        renderMessagesList(activeMessages)}

      {index === highlightTypes.pinboard && (
        <PinnedMessagesScreen
          // @ts-ignore
          disableBind={true}
          insideTabContainer={true}
          renderChatItem={renderItem}
        />
      )}

      {index === 2 && (
        // @ts-ignore
        <UserMentionsScreen handleClose={() => {}} />
      )}

      {index === 3 && <UserNudgesScreen />}
      {index === 4 && <SuggestionsScreen renderChatItem={renderItem} />}
    </View>
  )
}

export default DiscussionCardView
